import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ITag } from '@shared/models/api/base';
import { IPrinter, PrintService } from '@shared/services/print.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'app-edit-printer',
	templateUrl: './edit-printer.component.html',
	styleUrl: './edit-printer.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditPrinterComponent implements OnInit, OnDestroy {
	@Output() edited: EventEmitter<any> = new EventEmitter();
	@ViewChild('encodingInput', { static: false })
	encodingInput!: ElementRef<HTMLInputElement>;

	form: FormGroup;
	loading: boolean = false;
	public printer: IPrinter;
	httpError: string = '';
	private destroy$: Subject<void> = new Subject<void>();
	encodings: string[] = ['UTF-8', 'ASCII', 'windows-1252'];
	newTagName: string = '';
	newTagValue: string = '';
	tagError: string = '';

	constructor(
		@Inject(MAT_DIALOG_DATA) public editPrinter: IPrinter,
		public dialogRef: MatDialogRef<EditPrinterComponent>,
		private printService: PrintService,
		private fb: FormBuilder,
		private translateService: TranslateService,
	) {
		this.printer = {
			...editPrinter,
			tags: editPrinter.tags ?? [],
		};
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.printer.alias, [Validators.maxLength(255), Validators.required, Validators.pattern(ValidatorPatterns.Description)]],
			description: [this.printer.description, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			encoding: [this.printer.encoding, [Validators.required, Validators.maxLength(255)]],
			printerPath: [this.printer.printerPath, [Validators.required, Validators.maxLength(255)]],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				this.printer[key] = value[key];
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	onCancel = () => this.dialogRef.close();

	async onSave() {
		if (this.loading) return;
		this.httpError = '';
		if (this.form.valid) {
			this.loading = true;
			this.printService
				.updatePrinter(this.printer)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: () => {
						this.edited.emit();
						this.dialogRef.close();
					},
					error: (errorResponse) => {
						this.httpError = `${errorResponse.status}`;
						this.loading = false;
					},
				});
		}
	}

	openAutocomplete() {
		this.encodingInput.nativeElement.focus();
	}

	addTag(): void {
		const name = this.newTagName.trim();
		const value = this.newTagValue.trim();
		this.tagError = '';
		if (!name || !value) return;

		// Mirrors the backend PrinterValidator rule: tag names must be unique per printer,
		// case-insensitive - caught here instead of surfacing as a generic 400 at save time.
		if (this.printer.tags.some((tag) => tag.name.toLowerCase() === name.toLowerCase())) {
			this.tagError = 'mes.administration.printer.dialog.tags.duplicate';
			return;
		}

		this.printer.tags = [...this.printer.tags, { name, value }];
		this.newTagName = '';
		this.newTagValue = '';
	}

	removeTag(tag: ITag): void {
		this.printer.tags = this.printer.tags.filter((t) => t !== tag);
	}
}
